import type { Sprite } from 'pixi.js';
import { Ticker } from 'pixi.js';

export type ParticleInitConfig = {
  velocity:
    | { x: number | number[]; y: number | number[]; drag: number }
    | { angle: number | number[]; distance: number | number[]; drag: number };
  gravity: number;
  angularVelocity: number;
  angularDrag: number;
};

type ParticleData = {
  velX: number;
  velY: number;
  velDrag: number;
  gravity: number;
  angleVel: number;
  angleDrag: number;
};

const particleTicker = new Ticker();
particleTicker.start();

function parseNumberOrRange(value: number | number[]): number {
  if (Array.isArray(value)) {
    return value[0] + Math.random() * (value[1] - value[0]);
  }
  return value;
}

const activeSprites = new Map<Sprite, ParticleData>();

export function turnIntoParticle(sprite: Sprite, config: ParticleInitConfig): void {
  const data = initParticleData(config);
  if (sprite.destroyed) return;
  activeSprites.set(sprite, data);
  sprite.once('destroyed', () => activeSprites.delete(sprite));
  sprite.once('removed', () => activeSprites.delete(sprite));
}

function updateParticles(): void {
  if (activeSprites.size === 0) return;
  activeSprites.forEach((data, sprite) => {
    updateParticle(sprite, data, particleTicker.deltaMS);
  });
}
particleTicker.add(updateParticles);

function initParticleData(config: ParticleInitConfig): ParticleData {
  const data: ParticleData = {
    velX: 0,
    velY: 0,
    velDrag: 1,
    gravity: config.gravity,
    angleVel: config.angularVelocity,
    angleDrag: config.angularDrag,
  };

  if ('x' in config.velocity) {
    data.velX = parseNumberOrRange(config.velocity.x);
    data.velY = parseNumberOrRange(config.velocity.y);
    data.velDrag = config.velocity.drag;
  } else {
    const angle = parseNumberOrRange(config.velocity.angle);
    const distance = parseNumberOrRange(config.velocity.distance);
    data.velX = Math.cos(angle) * distance;
    data.velY = Math.sin(angle) * distance;
  }

  return data;
}

export function updateParticle(sprite: Sprite, data: ParticleData, delta: number): void {
  if (sprite.destroyed) return;

  sprite.x += data.velX * delta;
  sprite.y += data.velY * delta;

  data.velY += data.gravity * delta;

  data.velX *= data.velDrag;
  data.velY *= data.velDrag;

  sprite.rotation += data.angleVel;
  data.angleVel *= data.angleDrag;
}
